import { Component, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { SessionService } from 'src/app/auth/session/session.service';
import { EntityType } from '../../core/settings/settings.service';
import {
  Entity,
  EntitySearchService,
} from '../../shared/entity-search/entity-search.service';
import {
  Cohort,
  CohortEntity,
  CohortRequest,
  CohortService,
} from '../services/cohort.service';
import { CohortBulkImportComponent } from './bulk-cohort-add/bulk-cohort-add.component';

// Entity lookups go through the entity search batch endpoint in chunks so large
// bulk imports (tens of thousands of serials) resolve in seconds, not hours.
export const ENTITY_LOOKUP_BATCH_SIZE = 2500;
export const ENTITY_LOOKUP_CONCURRENCY = 3;

// Entity search stores group ids as Guid.ToString() (lowercase, no braces) and
// devices by serial number (uppercase); lookups are exact, so ids are normalized first.
const GUID_REGEX =
  /^\{?[0-9a-fA-F]{8}-(?:[0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}\}?$/;

interface EntityLookupChunk {
  entityType: EntityType;
  items: EntityLookupItem[];
}

interface EntityLookupItem {
  cohortEntity: CohortEntity;
  lookupId: string;
}

@Component({
  selector: 'dcc-create-cohort',
  templateUrl: './create-cohort.component.html',
  styleUrls: ['./create-cohort.component.scss'],
})
export class CreateCohortComponent implements OnInit {
  cohort: Cohort;
  unknownEntities: CohortEntity[] = [];
  entitySearchTypes = [EntityType.company, EntityType.group, EntityType.device];
  isUpdate = false;
  entities: Entity[] = [];
  serialNumbersString: string;
  groupIdsString: string;
  status: string;
  isLoadingEntities = false;
  entitiesLoaded = 0;
  entitiesToLoad = 0;
  private failedLookupChunks: EntityLookupChunk[] = [];

  constructor(
    private activeModal: NgbActiveModal,
    private cohortService: CohortService,
    private entityService: EntitySearchService,
    private modalService: NgbModal,
    private sessionService: SessionService,
    private toastr: ToastrService,
  ) {}

  get entityLoadFailed(): boolean {
    return this.failedLookupChunks.length > 0;
  }

  // Saving is blocked until every entity has been resolved, otherwise the cohort
  // would be created/updated with only the entities loaded so far.
  get canSave(): boolean {
    return !this.isLoadingEntities && !this.entityLoadFailed;
  }

  async ngOnInit() {
    if (this.cohort !== undefined) {
      this.isUpdate = true;
      await this.loadData();
    } else {
      this.cohort = {
        name: '',
        description: '',
        entities: [],
        createdDate: '',
        priority: null,
      };
    }
  }

  close(success = false) {
    const result: CreateCohortResult = { success };
    this.activeModal.close(result);
  }

  async save() {
    if (!this.validateCohort()) {
      return;
    }

    const cohort: CohortRequest = {
      cohortName: this.cohort.name,
      cohortDescription: this.cohort.description,
      entities: this.convertToCohortEntities(),
      requestCreator: this.getUserName(),
      priority: this.cohort.priority,
    };

    try {
      if (!this.isUpdate) {
        await this.cohortService.createCohort(cohort);
        this.toastr.success('Created cohort: ' + this.cohort.name);
      } else {
        cohort.hasPendingApproval = this.cohort.hasPendingApproval;
        await this.cohortService.updateCohort(cohort);
        this.toastr.success('Updated cohort: ' + this.cohort.name);
      }

      this.close(true);
    } catch (e) {
      if (!this.isUpdate) {
        if (e.status === 500) {
          this.toastr.error('Cohort with provided name already exists');
        } else {
          this.toastr.error('Failed to create Cohort:' + e.message);
        }
      } else {
        this.toastr.error('Failed to update Cohort');
      }

      this.close(false);
    }
  }

  onEntitySelected(entity: Entity) {
    this.addResolvedEntity(
      entity,
      this.keysOf(this.entities.map((e) => this.toCohortEntity(e))),
    );
  }

  removeEntity(entity: Entity) {
    this.entities = this.entities.filter((e) => e != entity);
  }

  removeCohortEntity(cohortEntity: CohortEntity) {
    this.unknownEntities = this.unknownEntities.filter(
      (e) => e != cohortEntity,
    );
  }

  openBulkModal() {
    if (this.isLoadingEntities) {
      return;
    }

    const modal = this.modalService.open(CohortBulkImportComponent, {
      size: 'lg',
      windowClass: 'lytxModal',
      centered: true,
    });
    modal.componentInstance.bulkEntitiesEvent.subscribe(
      async (bulkEntities) => {
        await this.parseBulkInput(bulkEntities);
      },
    );
  }

  async retryEntityLoad() {
    const chunks = this.failedLookupChunks;
    this.failedLookupChunks = [];
    const notFound = await this.runEntityLookups(chunks);
    if (notFound > 0) {
      this.toastr.error(`${notFound} entities not found`);
    }
    this.notifyLoadFailure();
  }

  private async loadData() {
    await this.getEntityData();
  }

  private async parseBulkInput(bulkEntities?: CohortEntity[]) {
    const notFound = await this.resolveEntities(bulkEntities ?? [], true);
    if (notFound > 0) {
      this.toastr.error(`${notFound} entities not found`);
    }
    this.notifyLoadFailure();
  }

  private async getEntityData() {
    await this.resolveEntities(this.cohort.entities ?? [], false);
    this.notifyLoadFailure();
  }

  // Resolves cohort entities through the batch lookup and returns the number not found.
  // Entities that are not found are kept as unknown entities so they are not dropped on save.
  // Bulk imports keep the normalized id/type for unknown entities; existing cohort members
  // are kept exactly as stored.
  private async resolveEntities(
    cohortEntities: CohortEntity[],
    isBulkImport: boolean,
  ): Promise<number> {
    // Entities queued for retry are excluded so they are only resolved through retry
    const seen = isBulkImport
      ? this.currentEntityKeys(
          this.failedLookupChunks.flatMap((c) =>
            c.items.map((i) => i.cohortEntity),
          ),
        )
      : new Set<string>();
    const itemsByType = new Map<EntityType, EntityLookupItem[]>();

    for (const cohortEntity of cohortEntities) {
      const key = this.entityKey(cohortEntity);
      if (!cohortEntity?.id || seen.has(key)) {
        continue;
      }
      seen.add(key);

      const entityType = this.toLookupEntityType(cohortEntity);
      if (!entityType) {
        this.unknownEntities.push(cohortEntity);
        continue;
      }

      const lookupId = this.toLookupId(cohortEntity.id, entityType);
      const items = itemsByType.get(entityType) ?? [];
      items.push({
        cohortEntity: isBulkImport
          ? {
              id: lookupId,
              type: entityType === EntityType.group ? 'group' : 'device',
            }
          : cohortEntity,
        lookupId,
      });
      itemsByType.set(entityType, items);
    }

    const chunks: EntityLookupChunk[] = [];
    itemsByType.forEach((items, entityType) => {
      for (let i = 0; i < items.length; i += ENTITY_LOOKUP_BATCH_SIZE) {
        chunks.push({
          entityType,
          items: items.slice(i, i + ENTITY_LOOKUP_BATCH_SIZE),
        });
      }
    });

    return await this.runEntityLookups(chunks);
  }

  private async runEntityLookups(chunks: EntityLookupChunk[]): Promise<number> {
    if (!chunks.length) {
      return 0;
    }

    this.isLoadingEntities = true;
    this.entitiesLoaded = 0;
    this.entitiesToLoad = chunks.reduce((sum, c) => sum + c.items.length, 0);
    let notFound = 0;
    let next = 0;

    const worker = async () => {
      while (next < chunks.length) {
        // Await into a local first; `notFound += await ...` would read the total
        // before the await and lose counts from other workers
        const chunkNotFound = await this.lookupChunk(chunks[next++]);
        notFound += chunkNotFound;
      }
    };

    try {
      await Promise.all(
        Array.from(
          { length: Math.min(ENTITY_LOOKUP_CONCURRENCY, chunks.length) },
          worker,
        ),
      );
    } finally {
      this.isLoadingEntities = false;
    }

    return notFound;
  }

  private async lookupChunk(chunk: EntityLookupChunk): Promise<number> {
    try {
      const found =
        (await this.entityService.getEntitiesById(
          chunk.entityType,
          chunk.items.map((i) => i.lookupId),
        )) ?? [];
      const foundById = new Map(
        found
          .filter((e) => e?.id)
          .map((e) => [e.id.toLowerCase(), e] as [string, Entity]),
      );

      // Entities may have been added while this chunk was in flight (e.g. via the
      // entity picker before a retry), so dedupe against the current contents
      const resolvedKeys = this.keysOf(
        this.entities.map((e) => this.toCohortEntity(e)),
      );
      const unknownKeys = this.keysOf(this.unknownEntities);
      let notFound = 0;
      for (const item of chunk.items) {
        const entity = foundById.get(item.lookupId.toLowerCase());
        if (entity) {
          this.addResolvedEntity(entity, resolvedKeys, unknownKeys);
          continue;
        }

        const key = this.entityKey(item.cohortEntity);
        if (!resolvedKeys.has(key) && !unknownKeys.has(key)) {
          unknownKeys.add(key);
          this.unknownEntities.push(item.cohortEntity);
          notFound++;
        }
      }
      return notFound;
    } catch {
      this.failedLookupChunks.push(chunk);
      return 0;
    } finally {
      this.entitiesLoaded += chunk.items.length;
    }
  }

  private notifyLoadFailure() {
    if (this.entityLoadFailed) {
      const count = this.failedLookupChunks.reduce(
        (sum, c) => sum + c.items.length,
        0,
      );
      this.toastr.error(
        `Failed to load ${count} entities. Retry before saving the cohort.`,
      );
    }
  }

  private toLookupEntityType(
    cohortEntity: CohortEntity,
  ): EntityType | undefined {
    switch (cohortEntity.type?.toLowerCase()) {
      case 'device':
        // A GUID can never be a serial number, so treat it as a group id
        return GUID_REGEX.test(cohortEntity.id.trim())
          ? EntityType.group
          : EntityType.device;
      case 'group':
        return EntityType.group;
      default:
        return undefined;
    }
  }

  private toLookupId(id: string, entityType: EntityType): string {
    const trimmed = id.trim();
    return entityType === EntityType.group
      ? trimmed.replace(/^\{|\}$/g, '').toLowerCase()
      : trimmed.toUpperCase();
  }

  private entityKey(cohortEntity: CohortEntity): string {
    const rawId = cohortEntity?.id?.trim() ?? '';
    const type = GUID_REGEX.test(rawId)
      ? 'group'
      : cohortEntity?.type?.toLowerCase();
    return `${type}:${rawId.replace(/^\{|\}$/g, '').toLowerCase()}`;
  }

  // Adds a resolved entity unless it is already resolved. A matching unknown entity
  // (left over from a not-found lookup) is replaced by the resolved one.
  private addResolvedEntity(
    entity: Entity,
    resolvedKeys: Set<string>,
    unknownKeys: Set<string> = this.keysOf(this.unknownEntities),
  ) {
    const key = this.entityKey(this.toCohortEntity(entity));
    if (resolvedKeys.has(key)) {
      return;
    }
    if (unknownKeys.has(key)) {
      this.unknownEntities = this.unknownEntities.filter(
        (u) => this.entityKey(u) !== key,
      );
      unknownKeys.delete(key);
    }
    resolvedKeys.add(key);
    this.entities.push(entity);
  }

  private currentEntityKeys(additional: CohortEntity[] = []): Set<string> {
    return this.keysOf(this.convertToCohortEntities().concat(additional));
  }

  private keysOf(cohortEntities: CohortEntity[]): Set<string> {
    return new Set(cohortEntities.map((e) => this.entityKey(e)));
  }

  private toCohortEntity(e: Entity): CohortEntity {
    const isCompany = e.entityType.toLowerCase() === 'company';
    return {
      id: isCompany ? e.rootGroupId : e.id,
      type: isCompany ? 'group' : e.entityType.toLowerCase(),
    };
  }

  private convertToCohortEntities(): CohortEntity[] {
    return this.entities
      .filter((e) => e != null)
      .map((e) => this.toCohortEntity(e))
      .concat(this.unknownEntities);
  }

  private validateCohort(): boolean {
    const operationType = this.isUpdate ? 'Update' : 'Create';

    if (this.isLoadingEntities) {
      this.toastr.error(
        `Cannot ${operationType} Cohort while entities are still loading`,
      );
      return false;
    }
    if (this.entityLoadFailed) {
      this.toastr.error(
        `Cannot ${operationType} Cohort until all entities have loaded. Retry loading entities.`,
      );
      return false;
    }
    if (!this.cohort.name) {
      this.toastr.error(`Cannot ${operationType} Cohort without the name`);
      return false;
    }
    if (!this.cohort.description) {
      this.toastr.error(
        `Cannot ${operationType} Cohort without any description`,
      );
      return false;
    }
    if (!this.entities.length) {
      this.toastr.error(`Cannot ${operationType} Cohort without entities`);
      return false;
    }
    if (this.cohort.priority != null && (this.cohort.priority < 1 || this.cohort.priority > 100)) {
      this.toastr.error('Priority must be between 1 and 100');
      return false;
    }
    return true;
  }

  private getUserName(): string {
    return this.sessionService.getCurrentSession().name;
  }
}

export interface CreateCohortResult {
  success: boolean;
}
